import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import WebsiteFooter from '@/components/website/WebsiteFooter';
import {
  Menu,
  X,
  ArrowRight,
  Mail,
  Phone,
  Linkedin,
  Twitter,
  Facebook,
  ChevronRight,
  Moon,
  Sun,
  Users,
  TrendingUp,
  Wallet,
  FileText,
  BarChart3,
  ShoppingCart,
  Scale
} from 'lucide-react';
import { toast } from 'sonner';

// Navbar Component
export const Navbar = ({ transparent = true }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [theme, setTheme] = useState('light');
  const navigate = useNavigate();
  const location = useLocation();

  // Logo URLs
  const LOGO_DARK_BG = "https://customer-assets.emergentagent.com/job_aurous-ecosystem/artifacts/x28qd6xo_30882183-9DD0-40B4-98FF-3BB81A460020.png";
  const LOGO_LIGHT_BG = "https://customer-assets.emergentagent.com/job_01e8900b-dd68-4220-97d5-adc9134fd5fc/artifacts/o7zqp6wt_D469D6E7-D15E-45AE-A15A-66CBE7FDF89F.png";

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle hash scrolling after navigation
  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        const element = document.querySelector(location.hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location]);

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
    document.documentElement.classList.toggle('dark');
  };

  const handleHashLink = (e, href) => {
    e.preventDefault();
    if (href.startsWith('/#')) {
      const hash = href.substring(1); // Get #section
      if (location.pathname === '/') {
        // Already on homepage, just scroll
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        // Navigate to homepage then scroll
        navigate('/' + hash);
      }
    }
  };

  const navLinks = [
    { name: 'IT Solutions', href: '/#it-solutions' },
    { name: 'AI Services', href: '/#services' },
    { name: 'AI Talent', href: '/ai-talent', isRoute: true },
    { name: 'AI News', href: '/ai-news', isRoute: true },
    { name: 'Our Work', href: '/our-work', isRoute: true },
    { name: 'Clients', href: '/clients', isRoute: true },
    { name: 'Career', href: '/careers', isRoute: true },
    { name: 'Contact', href: '/#contact' },
  ];

  const products = [
    { name: 'MyHR Orbit', description: 'AI-Powered HR Management', href: 'https://myhrorbit.com', icon: Users, color: 'bg-blue-500', textColor: 'text-blue-400', external: true },
    { name: 'MyFinance Orbit', description: 'AI-Powered Financial Management', href: 'https://myfinanceorbit.com', icon: Wallet, color: 'bg-green-500', textColor: 'text-green-400', external: true },
    { name: 'MyProcurement Orbit', description: 'AI-Powered Procurement', href: 'https://procurement.aurousorbit.ai', icon: ShoppingCart, color: 'bg-orange-500', textColor: 'text-orange-400', external: true },
    { name: 'MyGovernance Orbit', description: 'AI-Powered Governance', href: 'https://governance.aurousorbit.ai', icon: Scale, color: 'bg-purple-500', textColor: 'text-purple-400', external: true },
  ];

  const isLight = transparent ? scrolled : true;

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      scrolled 
        ? 'bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl shadow-lg shadow-slate-900/5' 
        : (transparent ? 'bg-transparent' : 'bg-slate-900')
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" onClick={() => window.scrollTo(0, 0)} className="flex items-center gap-2 group">
            <img 
              src={isLight ? LOGO_LIGHT_BG : LOGO_DARK_BG}
              alt="Aurous"
              className="h-10 w-auto transition-all"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              link.isRoute ? (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all hover:bg-white/10 ${
                    scrolled ? 'text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400' : 'text-white/80 hover:text-white'
                  }`}
                >
                  {link.name}
                </Link>
              ) : (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => handleHashLink(e, link.href)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all hover:bg-white/10 ${
                    scrolled ? 'text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400' : 'text-white/80 hover:text-white'
                  }`}
                >
                  {link.name}
                </a>
              )
            ))}
            
            {/* Products Dropdown */}
            <div className="relative">
              <button
                onClick={() => setProductsOpen(!productsOpen)}
                onBlur={() => setTimeout(() => setProductsOpen(false), 200)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all hover:bg-white/10 flex items-center gap-1 ${
                  scrolled ? 'text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400' : 'text-white/80 hover:text-white'
                }`}
              >
                Products
                <ChevronRight className={`w-4 h-4 transition-transform ${productsOpen ? 'rotate-90' : ''}`} />
              </button>
              
              {productsOpen && (
                <div className="absolute top-full left-0 mt-2 w-80 bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 overflow-hidden z-50">
                  {products.map((product) => (
                    <Link
                      key={product.name}
                      to={product.href}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                    >
                      <div className={`w-10 h-10 rounded-xl ${product.color} flex items-center justify-center`}>
                        <product.icon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <div className={`font-medium text-slate-900 dark:text-white`}>{product.name}</div>
                        <div className="text-sm text-slate-500 dark:text-slate-400">{product.description}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* CTA Buttons + Theme Toggle */}
          <div className="hidden lg:flex items-center gap-3">
            <button
              onClick={toggleTheme}
              className={`p-2.5 rounded-xl transition-all ${
                scrolled 
                  ? 'bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700' 
                  : 'bg-white/10 hover:bg-white/20'
              }`}
            >
              {theme === 'dark' ? (
                <Sun className={`w-5 h-5 ${scrolled ? 'text-yellow-500' : 'text-yellow-300'}`} />
              ) : (
                <Moon className={`w-5 h-5 ${scrolled ? 'text-slate-600' : 'text-white'}`} />
              )}
            </button>
            
            <Link to="/login">
              <Button variant="ghost" className={`rounded-xl ${scrolled ? 'text-slate-600 dark:text-slate-300' : 'text-white'}`}>
                Login
              </Button>
            </Link>
            <a href="/#contact">
              <Button className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white">
                Talk to an Expert
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </div>

          <button className="lg:hidden p-2" onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? (
              <X className={`w-6 h-6 ${isLight ? 'text-slate-900' : 'text-white'}`} />
            ) : (
              <Menu className={`w-6 h-6 ${isLight ? 'text-slate-900' : 'text-white'}`} />
            )}
          </button>
        </div>

        {isOpen && (
          <div className="lg:hidden bg-slate-900 rounded-xl shadow-xl mt-2 p-4 border border-slate-700">
            {navLinks.map((link) => (
              link.isRoute ? (
                <Link
                  key={link.name}
                  to={link.href}
                  className="block py-3 px-4 text-slate-300 hover:bg-slate-800 rounded-lg"
                  onClick={() => setIsOpen(false)}
                >
                  {link.name}
                </Link>
              ) : (
                <a
                  key={link.name}
                  href={link.href}
                  className="block py-3 px-4 text-slate-300 hover:bg-slate-800 rounded-lg"
                  onClick={() => setIsOpen(false)}
                >
                  {link.name}
                </a>
              )
            ))}
          </div>
        )}
      </div>
    </nav>
  );
};

// Contact Section Component
export const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, formData);
      toast.success('Thank you! We will get back to you soon.');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-slate-800 border-t border-slate-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <p className="text-blue-400 font-semibold mb-4 tracking-wide uppercase text-sm">Contact Us</p>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Let's Talk</h2>
            <p className="text-xl text-slate-400 mb-10">
              Have a project in mind? We&apos;d love to hear from you. 
              Send us a message and we&apos;ll respond within 24 hours.
            </p>

            <div className="space-y-6">
              {[
                { icon: Mail, label: 'Email', value: 'info@aurousconsultancy.com' },
                { icon: Phone, label: 'Phone', value: '+60 3-1234 5678' },
              ].map((item, index) => {
                const Icon = item.icon;
                return (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">{item.label}</p>
                      <p className="text-lg font-medium text-white">{item.value}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-slate-900 rounded-2xl p-8 border border-slate-700">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Name *</label>
                  <Input
                    placeholder="Your name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="h-12 bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Email *</label>
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="h-12 bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Phone</label>
                  <Input
                    placeholder="+60 12-345 6789"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="h-12 bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Subject *</label>
                  <Input
                    placeholder="Project inquiry"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    className="h-12 bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Message *</label>
                <Textarea
                  placeholder="Tell us about your project..."
                  rows={5}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                />
              </div>

              <Button type="submit" size="lg" className="w-full bg-blue-600 hover:bg-blue-700 h-14" disabled={loading}>
                {loading ? 'Sending...' : 'Send Message'}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Main WebsiteLayout Component
export default function WebsiteLayout({ children, showContact = true, transparentNav = false }) {
  return (
    <div className="min-h-screen bg-slate-900">
      <Navbar transparent={transparentNav} />
      {children}
      {showContact && <ContactSection />}
      <WebsiteFooter />
    </div>
  );
}
